import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  addCampaignQuestion,
  updateCampaignQuestion,
  deleteCampaignQuestion,
  reorderCampaignQuestions,
  type CampaignQuestion,
} from "@/services/api/campaigns";
import { useTranslation } from "@/hooks/useTranslation";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  GripVertical,
  Loader2,
  Type,
  ListOrdered,
  CheckSquare,
  Pencil,
} from "lucide-react";

interface QuestionnaireBuilderProps {
  campaignUuid: string;
  questions: CampaignQuestion[];
  onQuestionsChange: () => void;
}

export function QuestionnaireBuilder({
  campaignUuid,
  questions,
  onQuestionsChange,
}: QuestionnaireBuilderProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<CampaignQuestion | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  // Form state for add/edit dialog
  const [formType, setFormType] = useState<"text" | "single_choice" | "multiple_choice">("text");
  const [formText, setFormText] = useState("");
  const [formOptions, setFormOptions] = useState<string[]>(["", ""]);
  const [formRequired, setFormRequired] = useState(false);

  const resetForm = () => {
    setFormType("text");
    setFormText("");
    setFormOptions(["", ""]);
    setFormRequired(false);
    setEditingQuestion(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (question: CampaignQuestion) => {
    setEditingQuestion(question);
    setFormType(question.type);
    setFormText(question.text);
    setFormOptions(question.options || ["", ""]);
    setFormRequired(question.required);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formText.trim()) {
      toast.error(t("admin.campaigns.questionnaire.textRequired"));
      return;
    }

    // Validate options for choice questions
    if (formType !== "text") {
      const validOptions = formOptions.filter((o) => o.trim());
      if (validOptions.length < 2) {
        toast.error(t("admin.campaigns.questionnaire.minOptionsRequired"));
        return;
      }
    }

    try {
      setSaving(true);

      const questionData = {
        type: formType,
        text: formText.trim(),
        options: formType !== "text" ? formOptions.filter((o) => o.trim()) : undefined,
        required: formRequired,
      };

      if (editingQuestion) {
        await updateCampaignQuestion(campaignUuid, editingQuestion.id, questionData);
        toast.success(t("admin.campaigns.questionnaire.questionUpdated"));
      } else {
        await addCampaignQuestion(campaignUuid, questionData);
        toast.success(t("admin.campaigns.questionnaire.questionAdded"));
      }

      setDialogOpen(false);
      resetForm();
      onQuestionsChange();
    } catch (error) {
      console.error("Failed to save question:", error);
      toast.error(t("admin.campaigns.questionnaire.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (questionId: number) => {
    try {
      setDeleting(questionId);
      await deleteCampaignQuestion(campaignUuid, questionId);
      toast.success(t("admin.campaigns.questionnaire.questionDeleted"));
      onQuestionsChange();
    } catch (error) {
      console.error("Failed to delete question:", error);
      toast.error(t("admin.campaigns.questionnaire.deleteFailed"));
    } finally {
      setDeleting(null);
    }
  };

  const handleReorder = async (dragIndex: number, dropIndex: number) => {
    if (dragIndex === dropIndex) return;

    const newOrder = [...questions];
    const [removed] = newOrder.splice(dragIndex, 1);
    newOrder.splice(dropIndex, 0, removed);

    try {
      await reorderCampaignQuestions(
        campaignUuid,
        newOrder.map((q) => q.id)
      );
      onQuestionsChange();
    } catch (error) {
      console.error("Failed to reorder questions:", error);
      toast.error(t("admin.campaigns.questionnaire.reorderFailed"));
    }
  };

  const addOption = () => {
    setFormOptions([...formOptions, ""]);
  };

  const removeOption = (index: number) => {
    if (formOptions.length <= 2) return;
    setFormOptions(formOptions.filter((_, i) => i !== index));
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...formOptions];
    newOptions[index] = value;
    setFormOptions(newOptions);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "text":
        return <Type className="h-4 w-4" />;
      case "single_choice":
        return <ListOrdered className="h-4 w-4" />;
      case "multiple_choice":
        return <CheckSquare className="h-4 w-4" />;
      default:
        return <Type className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "text":
        return t("admin.campaigns.questionnaire.typeText");
      case "single_choice":
        return t("admin.campaigns.questionnaire.typeSingleChoice");
      case "multiple_choice":
        return t("admin.campaigns.questionnaire.typeMultipleChoice");
      default:
        return type;
    }
  };

  return (
    <div className="space-y-4">
      {/* Questions List */}
      {questions.length > 0 ? (
        <div className="space-y-3">
          {questions.map((question, index) => (
            <Card key={question.id} className="relative">
              <CardHeader className="pb-2">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    className="mt-1 cursor-move text-muted-foreground hover:text-foreground"
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", index.toString());
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const dragIndex = parseInt(e.dataTransfer.getData("text/plain"));
                      handleReorder(dragIndex, index);
                    }}
                    draggable
                  >
                    <GripVertical className="h-5 w-5" />
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getTypeIcon(question.type)}
                      <CardTitle className="text-base">{question.text}</CardTitle>
                      {question.required && (
                        <span className="text-xs text-red-500">{t("admin.campaigns.questionnaire.requiredBadge")}</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {getTypeLabel(question.type)}
                      {question.options && question.options.length > 0 && (
                        <span className="ml-2">
                          {t("admin.campaigns.questionnaire.optionsCount", { count: question.options.length })}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(question)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(question.id)}
                      disabled={deleting === question.id}
                    >
                      {deleting === question.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-destructive" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {question.options && question.options.length > 0 && (
                <CardContent className="pt-0">
                  <div className="pl-8 text-sm text-muted-foreground">
                    {question.options.map((option, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded-full border border-muted-foreground flex-shrink-0" />
                        {option}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <div className="py-8 text-center border-2 border-dashed rounded-lg">
          <Type className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground mb-4">{t("admin.campaigns.questionnaire.noQuestionsYet")}</p>
        </div>
      )}

      {/* Add Question Button */}
      <Button onClick={openAddDialog} variant="outline" className="w-full">
        <Plus className="h-4 w-4 mr-2" />
        {t("admin.campaigns.questionnaire.addQuestion")}
      </Button>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion ? t("admin.campaigns.questionnaire.editQuestion") : t("admin.campaigns.questionnaire.addQuestion")}
            </DialogTitle>
            <DialogDescription>
              {t("admin.campaigns.questionnaire.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("admin.campaigns.questionnaire.questionType")}</Label>
              <Select
                value={formType}
                onValueChange={(v) => setFormType(v as "text" | "single_choice" | "multiple_choice")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">
                    <div className="flex items-center gap-2">
                      <Type className="h-4 w-4" />
                      {t("admin.campaigns.questionnaire.typeTextInput")}
                    </div>
                  </SelectItem>
                  <SelectItem value="single_choice">
                    <div className="flex items-center gap-2">
                      <ListOrdered className="h-4 w-4" />
                      {t("admin.campaigns.questionnaire.typeSingleChoice")}
                    </div>
                  </SelectItem>
                  <SelectItem value="multiple_choice">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="h-4 w-4" />
                      {t("admin.campaigns.questionnaire.typeMultipleChoice")}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="questionText">{t("admin.campaigns.questionnaire.questionText")}</Label>
              <Input
                id="questionText"
                value={formText}
                onChange={(e) => setFormText(e.target.value)}
                placeholder={t("admin.campaigns.questionnaire.questionPlaceholder")}
              />
            </div>

            {formType !== "text" && (
              <div className="space-y-2">
                <Label>{t("admin.campaigns.questionnaire.optionsLabel")}</Label>
                <div className="space-y-2">
                  {formOptions.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={option}
                        onChange={(e) => updateOption(index, e.target.value)}
                        placeholder={t("admin.campaigns.questionnaire.optionPlaceholder", { index: index + 1 })}
                      />
                      {formOptions.length > 2 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOption(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addOption}
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t("admin.campaigns.questionnaire.addOption")}
                </Button>
              </div>
            )}

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-base">{t("admin.campaigns.questionnaire.requiredLabel")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("admin.campaigns.questionnaire.requiredHint")}
                </p>
              </div>
              <Switch
                checked={formRequired}
                onCheckedChange={setFormRequired}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("common.saving")}
                </>
              ) : editingQuestion ? (
                t("admin.campaigns.questionnaire.updateButton")
              ) : (
                t("admin.campaigns.questionnaire.addButton")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
